import { EntryType, type ProtocolEntry } from "@atoms/atom-types";
import React, { useMemo } from "react";

interface EnhancedProtocolViewerProps {
  protocol: ProtocolEntry[];
  width?: string;
  error?: string;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  theme: any;
}

interface PermissionCheck {
  type: "entity" | "atom" | "field";
  entityType?: string;
  atomType?: string;
  field?: string;
  permission: string;
  result: boolean;
  userPermissions?: string[];
}

interface AtomUsage {
  atomType: string;
  operations: Set<string>;
  count: number;
}

const PHASE_COLORS = {
  [EntryType.RequestRead]: "#3b82f6", // blue
  [EntryType.RequestWrite]: "#2563eb", // darker blue
  [EntryType.ReadGranted]: "#10b981", // green
  [EntryType.WriteGranted]: "#059669", // darker green
  [EntryType.ReadReleased]: "#6b7280", // gray
  [EntryType.WriteReleased]: "#4b5563", // darker gray
  [EntryType.RequestRejected]: "#ef4444", // red
  [EntryType.DeadlockDetected]: "#dc2626", // darker red
  [EntryType.DeadlockResolved]: "#f59e0b", // yellow
  [EntryType.Unlocked]: "#8b5cf6", // purple
  [EntryType.Created]: "#06b6d4", // cyan
  [EntryType.DebugInfo]: "#ec4899", // pink
  [EntryType.PhaseEnded]: "#a855f7", // violet
} as const;

export const EnhancedProtocolViewer: React.FC<EnhancedProtocolViewerProps> = ({
  protocol,
  width = "100%",
  error,
  theme,
}) => {
  const analysis = useMemo(() => {
    if (!protocol || protocol.length === 0) {
      return {
        phases: [],
        permissions: new Map<string, PermissionCheck[]>(),
        atoms: new Map<string, AtomUsage>(),
        totalDuration: 0,
        minTime: 0,
        maxTime: 0,
      };
    }

    const minTime = Math.min(...protocol.map((e) => e.time));
    const maxTime = Math.max(...protocol.map((e) => e.time));
    const totalDuration = maxTime - minTime;

    // Group entries by phase
    const phases: {
      name: string;
      start: number;
      end: number;
      entries: ProtocolEntry[];
      type: EntryType;
    }[] = [];

    // Collect permissions and atoms
    const permissions = new Map<string, PermissionCheck[]>();
    const atoms = new Map<string, AtomUsage>();

    // Create phases based on PhaseEnded entries and atom operations
    let lastPhaseEnd = 0;
    const atomOperations: typeof phases = [];

    protocol.forEach((entry, _index) => {
      // Normalize time
      const normalizedEntry = { ...entry, time: entry.time - minTime };

      // Track atom operations for timeline
      if (entry.extraInfo?.match(/use\/\d+\/(\w+)/)) {
        const atomMatch = entry.extraInfo.match(/use\/\d+\/(\w+)/);
        let atomName = atomMatch?.[1];
        if (atomName) {
          // Capitalize and format atom name
          atomName = atomName.charAt(0).toUpperCase() + atomName.slice(1);
          if (!atomName.includes("Atom")) {
            atomName = atomName.replace("atom", "Atom");
          }

          const isWrite =
            entry.type === EntryType.RequestWrite ||
            entry.type === EntryType.WriteGranted;
          const operationType = isWrite ? "Write" : "Read";

          atomOperations.push({
            name: `${atomName} (${operationType})`,
            start: normalizedEntry.time,
            end: normalizedEntry.time + 1, // Give it minimal width
            entries: [normalizedEntry],
            type: entry.type,
          });
        }
      }

      if (entry.type === EntryType.PhaseEnded && entry.extraInfo) {
        // Create a phase for this segment
        const phaseEntries: ProtocolEntry[] = [];

        // Collect all entries since last phase end
        for (let i = 0; i < protocol.length; i++) {
          const e = protocol[i];
          if (!e) continue;
          const normalizedTime = e.time - minTime;
          if (
            normalizedTime >= lastPhaseEnd &&
            normalizedTime <= normalizedEntry.time
          ) {
            phaseEntries.push({ ...e, time: normalizedTime } as ProtocolEntry);
          }
        }

        phases.push({
          name: entry.extraInfo,
          start: lastPhaseEnd,
          end: normalizedEntry.time,
          entries: phaseEntries,
          type: entry.type,
        });

        lastPhaseEnd = normalizedEntry.time;
      }

      // Extract permission checks
      if (entry.debugInfos && entry.extraInfo?.includes("Permission check:")) {
        const permInfo = entry.debugInfos as unknown as PermissionCheck;
        const key = `${permInfo.type}:${permInfo.atomType || permInfo.entityType}${permInfo.field ? `.${permInfo.field}` : ""}`;
        if (!permissions.has(key)) {
          permissions.set(key, []);
        }
        permissions.get(key)?.push(permInfo);
      }

      // Check for error debug infos
      if (
        entry.debugInfos &&
        (entry.debugInfos.errorType || entry.debugInfos.errorCode)
      ) {
        // This is an error entry
        phases.push({
          name: `ERROR: ${entry.extraInfo || "Unknown error"}`,
          start: normalizedEntry.time,
          end: normalizedEntry.time + 1,
          entries: [normalizedEntry],
          type: EntryType.DebugInfo,
        });
      }

      // Extract atom usage from various entry types
      if (entry.extraInfo) {
        // Check for atom operations in extraInfo
        if (
          entry.extraInfo.includes("aquireAtom") ||
          entry.extraInfo.includes("spawnAtom")
        ) {
          const match = entry.extraInfo.match(/\[(.*?)\]/);
          const atomType = match?.[1];
          if (atomType) {
            if (!atoms.has(atomType)) {
              atoms.set(atomType, {
                atomType,
                operations: new Set(),
                count: 0,
              });
            }
            const atom = atoms.get(atomType);
            if (atom) {
              atom.operations.add(
                entry.extraInfo.includes("spawn") ? "create" : "read",
              );
              atom.count++;
            }
          }
        }

        // Check for write operations
        if (entry.extraInfo.includes("update")) {
          const match = entry.extraInfo.match(/\[(.*?)\]/);
          const atomType = match?.[1];
          if (atomType) {
            if (!atoms.has(atomType)) {
              atoms.set(atomType, {
                atomType,
                operations: new Set(),
                count: 0,
              });
            }
            atoms.get(atomType)?.operations.add("write");
          }
        }

        // Check for atom paths that might indicate operations
        // Format: "use/291457520122200066/recipesuggestionlistatom"
        const atomPathMatch = entry.extraInfo.match(/use\/\d+\/(\w+)/);
        const atomTypeLower = atomPathMatch?.[1];
        if (atomTypeLower) {
          // Try to find the matching atom type (case-insensitive)
          let atomType = null;

          // Check if we already have this atom
          for (const [key, _value] of atoms.entries()) {
            if (
              key.toLowerCase() === atomTypeLower ||
              key.toLowerCase().replace("atom", "") ===
                atomTypeLower.replace("atom", "")
            ) {
              atomType = key;
              break;
            }
          }

          // If not found, create new entry with proper casing
          if (!atomType) {
            // Try to match with permission checks to get proper casing
            for (const [key, _checks] of permissions.entries()) {
              if (
                key.toLowerCase().includes(atomTypeLower.replace("atom", ""))
              ) {
                const parts = key.split(":");
                if (parts[1]) {
                  atomType = parts[1];
                  break;
                }
              }
            }

            // Default to capitalized version
            if (!atomType) {
              atomType =
                atomTypeLower.charAt(0).toUpperCase() + atomTypeLower.slice(1);
              if (!atomType.endsWith("Atom")) {
                atomType = atomType.replace("atom", "Atom");
              }
            }
          }

          if (!atoms.has(atomType)) {
            atoms.set(atomType, { atomType, operations: new Set(), count: 0 });
          }

          // Determine operation type based on entry type
          const atom = atoms.get(atomType);
          if (atom) {
            if (
              entry.type === EntryType.RequestWrite ||
              entry.type === EntryType.WriteGranted
            ) {
              atom.operations.add("write");
            } else if (
              entry.type === EntryType.RequestRead ||
              entry.type === EntryType.ReadGranted
            ) {
              atom.operations.add("read");
            }
            atom.count++;
          }
        }
      }
    });

    // If no phases were created, create one big phase
    if (phases.length === 0 && protocol.length > 0) {
      phases.push({
        name: protocol[0]?.extraInfo || "Operation",
        start: 0,
        end: totalDuration,
        entries: protocol.map((e) => ({ ...e, time: e.time - minTime })),
        type: EntryType.Created,
      });
    }

    // Merge atom operations into the visualization
    const allTimelineItems = [...phases, ...atomOperations].sort(
      (a, b) => a.start - b.start,
    );

    return {
      phases,
      atomOperations,
      allTimelineItems,
      permissions,
      atoms,
      totalDuration,
      minTime,
      maxTime,
    };
  }, [protocol]);

  const { phases, atomOperations, permissions, atoms, totalDuration } =
    analysis;

  return (
    <div style={{ padding: "1rem", width, overflowX: "auto" }}>
      {/* Error Display */}
      {error && (
        <div
          style={{
            marginBottom: "1rem",
            padding: "1rem",
            background: theme.errorColor,
            color: "white",
            borderRadius: "8px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <span style={{ fontSize: "1.2rem" }}>⚠️</span>
          <span>Error: {error}</span>
        </div>
      )}

      {/* Phase Timeline Bar */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h4
          style={{
            marginBottom: "0.5rem",
            fontSize: "0.9rem",
            fontWeight: "600",
            color: theme.text,
          }}
        >
          Execution Timeline
        </h4>

        {/* Phases Row */}
        <div style={{ marginBottom: "0.5rem" }}>
          <div
            style={{
              fontSize: "0.7rem",
              color: theme.subtextColor,
              marginBottom: "0.25rem",
            }}
          >
            Phases
          </div>
          <div
            style={{
              position: "relative",
              height: "30px",
              background: theme.cardBg,
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            {phases.map((phase) => {
              const startPercent = totalDuration
                ? (phase.start / totalDuration) * 100
                : 0;
              const widthPercent = totalDuration
                ? ((phase.end - phase.start) / totalDuration) * 100
                : 0;

              return (
                <div
                  key={phase.start}
                  style={{
                    position: "absolute",
                    left: `${startPercent}%`,
                    width: `${Math.max(widthPercent, 1)}%`,
                    height: "100%",
                    background: phase.name.startsWith("ERROR:")
                      ? "#dc2626"
                      : phase.name === "reducer"
                        ? "#3b82f6"
                        : phase.name === "persistence"
                          ? "#10b981"
                          : phase.name === "cache-update"
                            ? "#f59e0b"
                            : phase.name === "virtual-atoms"
                              ? "#8b5cf6"
                              : phase.name === "anouncements"
                                ? "#ec4899"
                                : phase.name === "redispubsub"
                                  ? "#06b6d4"
                                  : PHASE_COLORS[phase.type] || "#4b5563",
                    borderRight: "1px solid rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "4px",
                    overflow: "hidden",
                  }}
                  title={`${phase.name}: ${phase.end - phase.start}ms`}
                >
                  <span
                    style={{
                      fontSize: "0.65rem",
                      color: "white",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {phase.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Atom Operations Row */}
        <div style={{ marginBottom: "0.5rem" }}>
          <div
            style={{
              fontSize: "0.7rem",
              color: theme.subtextColor,
              marginBottom: "0.25rem",
            }}
          >
            Atom Operations
          </div>
          <div
            style={{
              position: "relative",
              height: "30px",
              background: theme.cardBg,
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            {atomOperations?.map((op) => {
              const startPercent = totalDuration
                ? (op.start / totalDuration) * 100
                : 0;
              const isWrite = op.name.includes("Write");

              return (
                <div
                  key={op.start}
                  style={{
                    position: "absolute",
                    left: `${startPercent}%`,
                    width: "2px",
                    height: "100%",
                    background: isWrite ? "#ef4444" : "#3b82f6",
                    zIndex: 2,
                  }}
                  title={op.name}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-2px",
                      left: "-4px",
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      background: isWrite ? "#dc2626" : "#2563eb",
                      border: "2px solid rgba(255,255,255,0.3)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "12px",
                      left: "10px",
                      fontSize: "0.6rem",
                      color: isWrite ? "#ef4444" : "#3b82f6",
                      whiteSpace: "nowrap",
                      background: "rgba(0,0,0,0.8)",
                      padding: "0 4px",
                      borderRadius: "2px",
                      transform: "rotate(-45deg)",
                      transformOrigin: "left center",
                    }}
                  >
                    {op.name.split(" ")[0]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            marginTop: "0.25rem",
            fontSize: "0.75rem",
            color: theme.subtextColor,
          }}
        >
          Total: {totalDuration}ms
        </div>
      </div>

      {/* Permissions Section */}
      {permissions.size > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h4
            style={{
              marginBottom: "0.5rem",
              fontSize: "0.9rem",
              fontWeight: "600",
              color: theme.text,
            }}
          >
            Permissions Checked
          </h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {Array.from(permissions.entries()).map(([key, checks]) => {
              const granted = checks.filter((c) => c.result).length;
              const denied = checks.filter((c) => !c.result).length;
              const [type, name] = key.split(":");

              return (
                <div
                  key={key}
                  style={{
                    padding: "0.25rem 0.5rem",
                    background:
                      granted > 0 && denied === 0
                        ? "#065f46"
                        : denied > 0
                          ? "#7f1d1d"
                          : "#374151",
                    color: "white",
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                  title={`${granted} granted, ${denied} denied\nPermissions: ${checks[0]?.permission || "N/A"}`}
                >
                  <span style={{ fontWeight: "600" }}>
                    {type?.toUpperCase()}
                  </span>
                  <span>{name}</span>
                  {granted > 0 && (
                    <span style={{ color: "#10b981" }}>✓{granted}</span>
                  )}
                  {denied > 0 && (
                    <span style={{ color: "#ef4444" }}>✗{denied}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Atoms Section */}
      {atoms.size > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h4
            style={{
              marginBottom: "0.5rem",
              fontSize: "0.9rem",
              fontWeight: "600",
              color: theme.text,
            }}
          >
            Atoms Accessed
          </h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {Array.from(atoms.values()).map((atom) => {
              const operations = Array.from(atom.operations);
              const bgColor =
                operations.includes("write") || operations.includes("create")
                  ? "#7c2d12"
                  : "#1e3a8a";

              return (
                <div
                  key={atom.atomType}
                  style={{
                    padding: "0.25rem 0.5rem",
                    background: bgColor,
                    color: "white",
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                  title={`Operations: ${operations.join(", ")}\nAccessed ${atom.count} time(s)`}
                >
                  <span>{atom.atomType}</span>
                  <span style={{ fontSize: "0.65rem", opacity: 0.8 }}>
                    {operations.includes("create") && "C"}
                    {operations.includes("read") && "R"}
                    {operations.includes("write") && "W"}
                    {atom.count > 1 && ` ×${atom.count}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detailed Timeline (existing view) */}
      <details>
        <summary
          style={{
            cursor: "pointer",
            marginBottom: "0.5rem",
            fontSize: "0.9rem",
            fontWeight: "600",
            color: theme.text,
          }}
        >
          Detailed Timeline
        </summary>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 200px",
            rowGap: "0.5rem",
            columnGap: "0.5rem",
            alignItems: "center",
            marginTop: "0.5rem",
          }}
        >
          {protocol.map((entry) => {
            const normalizedTime = entry.time - analysis.minTime;
            const computedMargin = totalDuration
              ? (normalizedTime / totalDuration) * 100
              : 0;

            return (
              <React.Fragment key={entry.time}>
                <div
                  style={{
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                    color: theme.text,
                  }}
                >
                  {EntryType[entry.type]}
                  {entry.extraInfo ? ` - ${entry.extraInfo}` : ""}
                </div>
                <div
                  style={{
                    position: "relative",
                    height: "2rem",
                    background: theme.cardBg,
                    borderRadius: "4px",
                    backgroundImage:
                      "repeating-linear-gradient(90deg, transparent, transparent 9px, rgba(0,0,0,0.05) 10px)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: `${computedMargin}%`,
                      top: 0,
                      bottom: 0,
                      width: "4px",
                      backgroundColor: PHASE_COLORS[entry.type] || "#3b82f6",
                      borderRadius: "2px",
                    }}
                    title={`Time: ${normalizedTime}ms\nLocker: ${entry.lockerId}\nType: ${entry.type}${entry.extraInfo ? `\nInfo: ${entry.extraInfo}` : ""}${entry.conflictingLockers?.length ? `\nConflicts: ${entry.conflictingLockers.join(", ")}` : ""}${entry.debugInfos ? `\n${JSON.stringify(entry.debugInfos, null, 2)}` : ""}`}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </details>
    </div>
  );
};
